/**
 * 회원가입 폼 검증 유틸
 */

import { SignupFormData, RegistrationType } from "../model/signup-types";
import { FORM_VALIDATION_RULES, ERROR_MESSAGES } from "../constants";

export const validateEmail = (email: string): string | null => {
  if (!email) return ERROR_MESSAGES.EMAIL_REQUIRED;
  if (!FORM_VALIDATION_RULES.EMAIL_REGEX.test(email))
    return ERROR_MESSAGES.EMAIL_INVALID;
  return null;
};

export const validatePhone = (phone: string): string | null => {
  if (!phone) return ERROR_MESSAGES.PHONE_REQUIRED;
  if (!FORM_VALIDATION_RULES.PHONE_REGEX.test(phone))
    return ERROR_MESSAGES.PHONE_INVALID;
  return null;
};

export const validatePassword = (
  password: string,
  passwordConfirm: string,
  isOptional: boolean = false
): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!password && !isOptional) {
    errors.password = ERROR_MESSAGES.PASSWORD_REQUIRED;
  } else if (
    password &&
    password.length < FORM_VALIDATION_RULES.PASSWORD_MIN_LENGTH
  ) {
    errors.password = ERROR_MESSAGES.PASSWORD_MIN_LENGTH;
  }

  if (password && password !== passwordConfirm) {
    errors.passwordConfirm = ERROR_MESSAGES.PASSWORD_MISMATCH;
  }

  return errors;
};

export const validateForm = (
  formData: SignupFormData,
  isEditMode: boolean,
  changePassword: boolean,
  type: RegistrationType
): Record<string, string> => {
  const newErrors: Record<string, string> = {};

  // 이름 검증 (edit 모드에서는 선택적)
  if (!isEditMode && !formData.username) {
    newErrors.username = ERROR_MESSAGES.USERNAME_REQUIRED;
  }

  // 휴대전화 검증 (edit 모드에서는 선택적)
  if (!isEditMode) {
    const phoneError = validatePhone(formData.user_phone);
    if (phoneError) newErrors.user_phone = phoneError;
  }

  // 이메일 검증 (edit 모드에서는 선택적)
  if (!isEditMode) {
    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;
  }

  // 성별 검증 (edit 모드에서는 선택적)
  if (!isEditMode && !formData.gender) {
    newErrors.gender = ERROR_MESSAGES.GENDER_REQUIRED;
  }

  // 아이디 검증 (edit 모드에서는 선택적)
  if (!isEditMode) {
    if (!formData.user_id) {
      newErrors.userId = ERROR_MESSAGES.USER_ID_REQUIRED;
    } else if (
      formData.user_id.length < FORM_VALIDATION_RULES.USER_ID_MIN_LENGTH
    ) {
      newErrors.userId = ERROR_MESSAGES.USER_ID_MIN_LENGTH;
    }
  }

  // 비밀번호 검증 (edit 모드에서는 변경할 때만)
  if (isEditMode) {
    if (changePassword) {
      const passwordErrors = validatePassword(
        formData.password,
        formData.passwordConfirm,
        false
      );
      Object.assign(newErrors, passwordErrors);
    }
  } else {
    const passwordErrors = validatePassword(
      formData.password,
      formData.passwordConfirm,
      false
    );
    Object.assign(newErrors, passwordErrors);
  }

  // 주소 검증 (edit 모드에서는 선택적)
  if (!isEditMode) {
    if (!formData.address) {
      newErrors.address = ERROR_MESSAGES.ADDRESS_REQUIRED;
    }
    if (!formData.address_detail) {
      newErrors.address_detail = ERROR_MESSAGES.ADDRESS_DETAIL_REQUIRED;
    }
  }

  // 담당자 정보 검증 (유치원, 프랜차이즈일 때만)
  if ((type === "kindergarten" || type === "franchise") && !isEditMode) {
    if (!formData.manager_name) {
      newErrors.manager_name = ERROR_MESSAGES.MANAGER_NAME_REQUIRED;
    }
    if (!formData.manager_phone) {
      newErrors.manager_phone = ERROR_MESSAGES.MANAGER_PHONE_REQUIRED;
    } else if (!/^01[0-9]-?\d{3,4}-?\d{4}$/.test(formData.manager_phone)) {
      newErrors.manager_phone = ERROR_MESSAGES.MANAGER_PHONE_INVALID;
    }
  }

  // 약관 동의 검증 (signup 모드에서만)
  if (!isEditMode) {
    if (!formData.isEmailVerified) {
      newErrors.isEmailVerified = ERROR_MESSAGES.EMAIL_REQUIRED;
    }
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = ERROR_MESSAGES.TERMS_REQUIRED;
    }
    if (!formData.agreeToPrivacy) {
      newErrors.agreeToPrivacy = ERROR_MESSAGES.PRIVACY_REQUIRED;
    }
  }

  return newErrors;
};
